import { useEffect, useState } from 'react';
import type { WallArtworkBackdrop, WallArtworkPlan } from './wallArtwork';
import { loadWallImage, renderWallArtworkBackdrop } from './wallArtwork';

/** A new world revision immediately makes the old reference unavailable, before the effect runs. */
export function useWallArtwork(plan: WallArtworkPlan | null, enabled: boolean) {
  const [retry, setRetry] = useState(0);
  const [result, setResult] = useState<{ key: string; backdrop: WallArtworkBackdrop | null; error: string }>({ key: '', backdrop: null, error: '' });
  const key = enabled && plan ? `${plan.key}:${retry}` : '';
  useEffect(() => {
    if (!key || !plan) return;
    const controller = new AbortController();
    const backdrop = { below: document.createElement('canvas'), above: document.createElement('canvas') };
    setResult({ key, backdrop: null, error: '' });
    void renderWallArtworkBackdrop(plan, backdrop, loadWallImage, controller.signal)
      .then(backdrop => { if (!controller.signal.aborted) setResult({ key, backdrop, error: '' }); })
      .catch(error => { if (!controller.signal.aborted) setResult({ key, backdrop: null, error: error instanceof Error ? error.message : 'Wall artwork is unavailable. Retry.' }); });
    return () => { controller.abort(); for (const canvas of [backdrop.below, backdrop.above]) { canvas.width = 0; canvas.height = 0; } };
    // All consequential input is in the immutable plan key; presence/brush changes cannot restart loading.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  const current = result.key === key && enabled ? result : null;
  const backdrop = current?.backdrop && [current.backdrop.below, current.backdrop.above].every(canvas => canvas.width === plan?.width && canvas.height === plan?.height) ? current.backdrop : null;
  return { backdrop, error: current?.error ?? '', ready: !!backdrop,
    retry: () => setRetry(value => value + 1) };
}
